<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatTime } from '@chatwoot/utils';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';
import { useAlert } from 'dashboard/composables';
import ReportsAPI from 'dashboard/api/reports';
import SummaryReportsAPI from 'dashboard/api/summaryReports';
import { getReadableInboxByType } from 'dashboard/helper/inbox';

import ReportHeader from './components/ReportHeader.vue';
import ReportFilters from './components/ReportFilters.vue';
import ConversationHeatmapContainer from './components/heatmaps/ConversationHeatmapContainer.vue';
import ChartTooltip from './components/operations/ChartTooltip.vue';
import KpiCard from './components/operations/KpiCard.vue';
import FlowChart from './components/operations/FlowChart.vue';
import FirstResponseChart from './components/operations/FirstResponseChart.vue';
import SpreadChart from './components/operations/SpreadChart.vue';
import RankedBars from './components/operations/RankedBars.vue';
import AgentPerformanceTable from './components/operations/AgentPerformanceTable.vue';
import AgentPresenceHeatmap from './components/operations/AgentPresenceHeatmap.vue';
import CsatWeeklyChart from './components/operations/CsatWeeklyChart.vue';
import { useChartTooltip } from './composables/useChartTooltip';
import {
  formatCount,
  formatPercent,
  percentOf,
} from './helpers/operationsChartHelper';

const store = useStore();
const { t, locale } = useI18n();
const { tooltip, onPointerMove, onPointerLeave } = useChartTooltip();

const from = ref(0);
const to = ref(0);
const businessHours = ref(false);
const inboxIds = ref([]);
const agentIds = ref([]);

const operations = ref({
  flow: [],
  firstResponse: [],
  firstResponseSummary: {},
  firstResponseSpread: [],
  csat: [],
});
const presence = ref({ daysInRange: 0, agents: [], unattributedMessages: 0 });
const channels = ref([]);
const inboxVolume = ref([]);

const liveMetrics = useMapGetter('getAccountConversationMetric');
const agents = useMapGetter('agents/getAgents');
const inboxes = useMapGetter('inboxes/getInboxes');
const accountSummary = useMapGetter('getAccountSummary');
const botSummary = useMapGetter('getBotSummary');
const agentSummaries = useMapGetter('summaryReports/getAgentSummaryReports');
const agentTimeSummary = useMapGetter('getAgentTimeSummary');
const csatSatisfaction = useMapGetter('csat/getSatisfactionScore');

const scope = () => ({ inboxIds: inboxIds.value, agentIds: agentIds.value });

// Availability is not something the API filters, so the roster is narrowed here - a panel filtered
// to two agents should not count the whole team as online.
const scopedAgents = computed(() =>
  agentIds.value.length
    ? agents.value.filter(agent => agentIds.value.includes(agent.id))
    : agents.value
);

const agentStatus = computed(() =>
  scopedAgents.value.reduce((counts, agent) => {
    counts[agent.availability_status] =
      (counts[agent.availability_status] || 0) + 1;
    return counts;
  }, {})
);

const requestPayload = () => ({
  from: from.value,
  to: to.value,
  businessHours: businessHours.value,
  ...scope(),
});

const delta = (current, previous) => {
  if (!previous || !Number.isFinite(current)) return null;
  return ((current - previous) / previous) * 100;
};

const duration = seconds =>
  Number.isFinite(seconds) && seconds > 0
    ? formatTime(Math.round(seconds))
    : '—';

const flow = computed(() => operations.value.flow);

// The percentile series only carries days that had a first response; the chart needs one entry per
// day of the range so the dates stay where they belong.
const firstResponsePoints = computed(() => {
  const byTimestamp = new Map(
    operations.value.firstResponse.map(point => [point.timestamp, point])
  );

  return flow.value.map(day => ({
    timestamp: day.timestamp,
    p50: byTimestamp.get(day.timestamp)?.p50 ?? null,
    p90: byTimestamp.get(day.timestamp)?.p90 ?? null,
    count: byTimestamp.get(day.timestamp)?.count ?? 0,
  }));
});

const botResolutionRate = computed(() => {
  const conversations = accountSummary.value.conversations_count;
  if (!conversations) return null;
  return percentOf(botSummary.value.bot_resolutions_count || 0, conversations);
});

const previousBotResolutionRate = computed(() => {
  const conversations = accountSummary.value.previous?.conversations_count;
  if (!conversations) return null;
  return percentOf(
    botSummary.value.previous?.bot_resolutions_count || 0,
    conversations
  );
});

const kpis = computed(() => {
  const summary = accountSummary.value;
  const previous = summary.previous || {};
  const firstResponse = operations.value.firstResponseSummary || {};

  return [
    {
      key: 'conversations',
      label: t('OPERATIONS_REPORTS.KPI.CONVERSATIONS'),
      value: formatCount(summary.conversations_count, locale.value),
      delta: delta(summary.conversations_count, previous.conversations_count),
      goodDirection: null,
      series: flow.value.map(day => day.conversationsCount),
      tone: 'blue',
    },
    {
      key: 'resolutions',
      label: t('OPERATIONS_REPORTS.KPI.RESOLUTIONS'),
      value: formatCount(summary.resolutions_count, locale.value),
      delta: delta(summary.resolutions_count, previous.resolutions_count),
      goodDirection: 'up',
      series: flow.value.map(day => day.resolutionsCount),
      tone: 'teal',
    },
    {
      key: 'first-response',
      label: t('OPERATIONS_REPORTS.KPI.FIRST_RESPONSE'),
      value: duration(firstResponse.p50),
      delta: delta(firstResponse.p50, firstResponse.previous?.p50),
      goodDirection: 'down',
      series: operations.value.firstResponse.map(point => point.p50),
      tone: 'blue',
    },
    {
      key: 'resolution-time',
      label: t('OPERATIONS_REPORTS.KPI.RESOLUTION_TIME'),
      value: duration(summary.avg_resolution_time),
      delta: delta(summary.avg_resolution_time, previous.avg_resolution_time),
      goodDirection: 'down',
      series: [],
      tone: 'blue',
    },
    {
      key: 'csat',
      label: t('OPERATIONS_REPORTS.KPI.CSAT'),
      value: csatSatisfaction.value
        ? `${csatSatisfaction.value}%`
        : t('OPERATIONS_REPORTS.KPI.NO_DATA'),
      delta: null,
      goodDirection: 'up',
      series: [],
      tone: 'teal',
    },
    {
      key: 'bot',
      label: t('OPERATIONS_REPORTS.KPI.BOT_RESOLUTIONS'),
      value:
        botResolutionRate.value === null
          ? t('OPERATIONS_REPORTS.KPI.NO_DATA')
          : `${formatPercent(botResolutionRate.value, locale.value)}%`,
      delta: delta(botResolutionRate.value, previousBotResolutionRate.value),
      goodDirection: 'up',
      series: [],
      tone: 'teal',
    },
  ];
});

const liveTiles = computed(() => [
  {
    key: 'unattended',
    label: t('OPERATIONS_REPORTS.LIVE.UNATTENDED'),
    value: liveMetrics.value.unattended,
    note: t('OPERATIONS_REPORTS.LIVE.UNATTENDED_NOTE'),
    alert: true,
  },
  {
    key: 'open',
    label: t('OPERATIONS_REPORTS.LIVE.OPEN'),
    value: liveMetrics.value.open,
    note: t('OPERATIONS_REPORTS.LIVE.OPEN_NOTE'),
  },
  {
    key: 'unassigned',
    label: t('OPERATIONS_REPORTS.LIVE.UNASSIGNED'),
    value: liveMetrics.value.unassigned,
    note: t('OPERATIONS_REPORTS.LIVE.UNASSIGNED_NOTE'),
  },
  {
    key: 'pending',
    label: t('OPERATIONS_REPORTS.LIVE.PENDING'),
    value: liveMetrics.value.pending,
    note: t('OPERATIONS_REPORTS.LIVE.PENDING_NOTE'),
  },
  {
    key: 'online',
    label: t('OPERATIONS_REPORTS.LIVE.ONLINE'),
    value: agentStatus.value.online || 0,
    note: t('OPERATIONS_REPORTS.LIVE.ONLINE_NOTE', {
      busy: agentStatus.value.busy || 0,
      offline: agentStatus.value.offline || 0,
    }),
  },
]);

// Named here rather than at fetch time: the inbox list can still be loading when the volumes land.
const namedInboxVolume = computed(() =>
  inboxVolume.value.map(item => ({
    name:
      inboxes.value.find(inbox => inbox.id === item.id)?.name ||
      t('OPERATIONS_REPORTS.VOLUME.UNKNOWN_INBOX'),
    value: item.value,
  }))
);

const fetchLiveData = () => {
  store.dispatch('fetchAccountConversationMetric', scope());
  store.dispatch('agents/get');
};

const { startRefetching } = useLiveRefresh(fetchLiveData);

const fetchOperations = async () => {
  try {
    const { data } = await ReportsAPI.getOperations(requestPayload());
    operations.value = {
      flow: data.flow.map(day => ({
        timestamp: day.timestamp,
        conversationsCount: day.conversations_count,
        resolutionsCount: day.resolutions_count,
        backlog: day.backlog,
      })),
      firstResponse: data.first_response,
      firstResponseSummary: data.first_response_summary,
      firstResponseSpread: data.first_response_spread,
      csat: data.csat,
    };
  } catch {
    useAlert(t('REPORT.DATA_FETCHING_FAILED'));
  }
};

const fetchPresence = async () => {
  try {
    const { data } = await ReportsAPI.getAgentPresence({
      from: from.value,
      to: to.value,
      ...scope(),
    });
    presence.value = {
      daysInRange: data.days_in_range,
      unattributedMessages: data.unattributed_messages,
      agents: data.agents.map(agent => ({
        id: agent.id,
        activeDays: agent.active_days,
        onlineSeconds: agent.online_seconds,
        busySeconds: agent.busy_seconds,
        messages: agent.messages,
        hours: agent.hours.map(hour => ({
          hour: hour.hour,
          onlineSeconds: hour.online_seconds,
          busySeconds: hour.busy_seconds,
          messages: hour.messages,
          days: hour.days,
        })),
      })),
    };
  } catch {
    useAlert(t('REPORT.DATA_FETCHING_FAILED'));
  }
};

const fetchVolumes = async () => {
  const params = {
    since: from.value,
    until: to.value,
    businessHours: businessHours.value,
    ...scope(),
  };

  try {
    const [channelResponse, inboxResponse] = await Promise.all([
      SummaryReportsAPI.getChannelReports(params),
      SummaryReportsAPI.getInboxReports(params),
    ]);

    channels.value = Object.entries(channelResponse.data)
      .map(([channelType, stats]) => ({
        name: t(
          `OPERATIONS_REPORTS.VOLUME.CHANNELS.${getReadableInboxByType(channelType).toUpperCase()}`
        ),
        value: stats.total,
      }))
      .sort((first, second) => second.value - first.value);

    inboxVolume.value = inboxResponse.data
      .map(report => ({ id: report.id, value: report.conversations_count }))
      .filter(item => item.value > 0)
      .sort((first, second) => second.value - first.value);
  } catch {
    useAlert(t('REPORT.DATA_FETCHING_FAILED'));
  }
};

const fetchPeriodData = () => {
  const payload = requestPayload();
  store.dispatch('fetchAccountSummary', { ...payload, type: 'account' });
  store.dispatch('fetchBotSummary', payload);
  store.dispatch('summaryReports/fetchAgentSummaryReports', {
    since: from.value,
    until: to.value,
    businessHours: businessHours.value,
    ...scope(),
  });
  store.dispatch('fetchAgentTimeSummary', {
    from: from.value,
    to: to.value,
    ...scope(),
  });
  store.dispatch('csat/getMetrics', {
    from: from.value,
    to: to.value,
    inbox_id: inboxIds.value,
    user_ids: agentIds.value,
  });
  fetchOperations();
  fetchPresence();
  fetchVolumes();
};

const onFilterChange = ({
  from: since,
  to: until,
  businessHours: hours,
  inboxIds: inboxes_,
  agentIds: agents_,
}) => {
  from.value = since;
  to.value = until;
  businessHours.value = hours;
  inboxIds.value = inboxes_;
  agentIds.value = agents_;
  fetchPeriodData();
  fetchLiveData();
};

onMounted(() => {
  // The live tiles are fetched by the filter change the ReportFilters child emits on its own mount,
  // which runs before this one - and which also needs this list, for its inbox chip.
  store.dispatch('inboxes/get');
  startRefetching();
});
</script>

<template>
  <ReportHeader :header-title="$t('OPERATIONS_REPORTS.HEADER')" />
  <div
    class="flex flex-col gap-6 pb-8"
    @pointermove="onPointerMove"
    @pointerleave="onPointerLeave"
  >
    <ReportFilters
      :show-entity-filter="false"
      :show-group-by="false"
      show-inbox-filter
      show-agent-filter
      @filter-change="onFilterChange"
    />

    <section class="flex flex-col gap-3">
      <div class="flex flex-wrap items-baseline gap-3">
        <h2
          class="mb-0 text-xs font-semibold tracking-wider uppercase text-n-slate-10"
        >
          {{ $t('OPERATIONS_REPORTS.LIVE.TITLE') }}
        </h2>
        <span class="inline-flex items-center gap-1.5 text-xs text-n-slate-10">
          <i class="w-1.5 h-1.5 rounded-full bg-n-teal-9" />
          {{ $t('OPERATIONS_REPORTS.LIVE.NOTE') }}
        </span>
      </div>
      <div class="grid gap-3 grid-cols-[repeat(auto-fit,minmax(11rem,1fr))]">
        <div
          v-for="tile in liveTiles"
          :key="tile.key"
          class="flex flex-col p-4 border rounded-xl border-n-weak bg-n-solid-2"
        >
          <p class="mb-1.5 text-xs text-n-slate-11">{{ tile.label }}</p>
          <div
            class="text-2xl font-semibold tracking-tight"
            :class="tile.alert ? 'text-n-ruby-11' : 'text-n-slate-12'"
          >
            {{ tile.value ?? '—' }}
          </div>
          <p class="mt-2 mb-0 text-xs text-n-slate-10">{{ tile.note }}</p>
        </div>
      </div>
    </section>

    <section
      class="grid gap-3 grid-cols-[repeat(auto-fit,minmax(11.5rem,1fr))]"
    >
      <KpiCard
        v-for="kpi in kpis"
        :key="kpi.key"
        :label="kpi.label"
        :value="kpi.value"
        :delta="kpi.delta"
        :good-direction="kpi.goodDirection"
        :series="kpi.series"
        :tone="kpi.tone"
      />
    </section>

    <FlowChart :days="flow" />

    <section class="grid gap-3 grid-cols-[repeat(auto-fit,minmax(21rem,1fr))]">
      <FirstResponseChart :points="firstResponsePoints" />
      <SpreadChart :buckets="operations.firstResponseSpread" />
    </section>

    <section class="grid gap-3 grid-cols-[repeat(auto-fit,minmax(21rem,1fr))]">
      <RankedBars
        :title="$t('OPERATIONS_REPORTS.VOLUME.CHANNEL_TITLE')"
        :subtitle="$t('OPERATIONS_REPORTS.VOLUME.CHANNEL_SUBTITLE')"
        :items="channels"
      />
      <RankedBars
        :title="$t('OPERATIONS_REPORTS.VOLUME.INBOX_TITLE')"
        :subtitle="$t('OPERATIONS_REPORTS.VOLUME.INBOX_SUBTITLE')"
        :items="namedInboxVolume"
      />
    </section>

    <ConversationHeatmapContainer :inbox-ids="inboxIds" :agent-ids="agentIds" />

    <AgentPresenceHeatmap :presence="presence" :agents="agents" />

    <AgentPerformanceTable
      :agents="agents"
      :summaries="agentSummaries"
      :time-summary="agentTimeSummary"
    />

    <CsatWeeklyChart :weeks="operations.csat" />

    <ChartTooltip :tooltip="tooltip" />
  </div>
</template>
